import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IAcademicStatus } from 'src/app/shared/models/academic-status.model';
import { IAddress } from 'src/app/shared/models/address.model';
import { IAdmissionsApplication, IFullAdmissionsApplication } from 'src/app/shared/models/admissions-application.model';
import { IAdmissionsDeposit } from 'src/app/shared/models/admissions-deposit.model';
import { IAdmissionsQuestion } from 'src/app/shared/models/admissions-question.model';
import { IAdvisor } from 'src/app/shared/models/advisor.model';
import { IAuditCourseHistory } from 'src/app/shared/models/audit-course-history.model';
import { IBio } from 'src/app/shared/models/bio.model';
import { ICitizenship } from 'src/app/shared/models/citizenship.model';
import { IComment } from 'src/app/shared/models/comment.model';
import { IConcealedCarryStatus } from 'src/app/shared/models/concealed-carry-status.model';
import { IDescription } from 'src/app/shared/models/description.model';
import { IFerpaPassword } from 'src/app/shared/models/ferpa-password';
import { IGunClubCertification } from 'src/app/shared/models/gun-club-certification.model';
import { IHighSchoolTranscript } from 'src/app/shared/models/high-school-transcript.model';
import { IHold } from 'src/app/shared/models/hold.model';
import { IHousingData } from 'src/app/shared/models/housing-data.model';
import { IId } from 'src/app/shared/models/id.model';
import { LibertyStudentSummary } from 'src/app/shared/models/liberty-student-summary.model';
import { PersonSearch } from 'src/app/shared/models/person-search.model';
import { IPriorColleges } from 'src/app/shared/models/prior-college.model';
import { ISanctionSummary } from 'src/app/shared/models/sanction-summary.model';
import { IStudentProgramList } from 'src/app/shared/models/student-program-list.model';
import { IStudentStatus } from 'src/app/shared/models/student-status.model';
import { ITestScore } from 'src/app/shared/models/test-score.model';
import { ITest } from 'src/app/shared/models/test.model';
import { IBasePerson } from 'src/app/shared/people/models/person';
import { environment } from 'src/environments/environment';
import { withParams } from '../functions/with-params.function';

@Injectable({
  providedIn: 'root'
})
export class PeopleRepository {

  private readonly repoUrl: string = `${environment.apiUrl}/people`;

  constructor(private http: HttpClient) { }

  public search(criteria: string): Observable<Array<IBasePerson>> {
    const personSearch: PersonSearch = {
      criteria: criteria,
      withIncidents: false
    };

    return this.http.post<Array<IBasePerson>>(this.repoUrl, personSearch);
  }

  public findByPidm(pidm: number): Observable<IBasePerson> {
    return this.http.get<IBasePerson>(`${this.repoUrl}/${pidm}`);
  }

  public ferpa(pidm: number): Observable<Array<IFerpaPassword>> {
    return this.http.get<Array<IFerpaPassword>>(`${this.repoUrl}/${pidm}/ferpa`);
  }

  public query(criteria: string, withIncidents: boolean): Observable<Array<IBasePerson>> {
    const personSearch: PersonSearch = {
      criteria: criteria,
      withIncidents: withIncidents
    };

    return this.http.post<Array<IBasePerson>>(this.repoUrl, personSearch);
  }

  public housing(pidm: number, term: string): Observable<IHousingData> {
    return this.http.get<IHousingData>(`${this.repoUrl}/${pidm}/housing-information`, withParams({ term }));
  }

  public findAdmissionsApplication(pidm: number, term: string, appNum: string): Observable<IFullAdmissionsApplication> {
    return this.http.get<IFullAdmissionsApplication>(`${this.repoUrl}/${pidm}/admissions-application`, withParams({ term, appNum }));
  }

  public findAdmissionsApplications(pidm: number): Observable<Array<IAdmissionsApplication>> {
    return this.http.get<Array<IAdmissionsApplication>>(`${this.repoUrl}/admissions-applications`, withParams({ pidm }));
  }

  public whoAmI(): Observable<IBasePerson> {
    return this.http.get<IBasePerson>(`${this.repoUrl}/me`);
  }

  public getBio(pidm:number): Observable<IBio> {
    return this.http.get<IBio>(`${this.repoUrl}/${pidm}/bio`);
  }

  public getEmail(pidm:number): Observable<IDescription> {
    return this.http.get<IDescription>(`${this.repoUrl}/${pidm}/email`);
  }

  public getHousing(student: IBasePerson, termCode: string): Observable<IHousingData> {
    return this.http.get<IHousingData>(`${this.repoUrl}/${student.pidm}/housing-information?term=${termCode}`);
  }

  public getPhone(student: IBasePerson, code?: string): Observable<IDescription> {
    return this.http.get<IDescription>(`${this.repoUrl}/${student.pidm}/phone${code ? '?code=' + code : ''}`);
  }

  public getAddress(student: IBasePerson, code?: string): Observable<IAddress> {
    return this.http.get<IAddress>(`${this.repoUrl}/${student.pidm}/address${code ? '?code=' + code : ''}`);
  }

  public getCitizenship(pidm:number): Observable<ICitizenship> {
    return this.http.get<ICitizenship>(`${this.repoUrl}/${pidm}/citizenship`);
  }

  public concealedCarryStatus(pidm:number): Observable<IConcealedCarryStatus> {
    return this.http.get<IConcealedCarryStatus>(`${this.repoUrl}/${pidm}/concealed-carry`);
  }

  public getGunClubCertification(pidm:number): Observable<IGunClubCertification> {
    return this.http.get<IGunClubCertification>(`${this.repoUrl}/${pidm}/gfs-certification`);
  }

  public findClassStatus(student: IBasePerson, termCode: string): Observable<IAcademicStatus> {
    return this.http.get<IAcademicStatus>(`${this.repoUrl}/${student.pidm}/class-status?term=${termCode}`);
  }
  
  public findPreviousLuoaOffenseCount(student: IBasePerson, termCode?: string): Observable<IId> {
    return this.http.get<IId>(`${this.repoUrl}/${student.pidm}/previous-luoa-offenses${termCode ? '?termCode=' + termCode : ''}`);
  }
  
  public findHolds(student: IBasePerson, termCode?: string): Observable<Array<IHold>> {
    return this.http.get<Array<IHold>>(`${this.repoUrl}/${student.pidm}/holds${termCode ? '?term=' + termCode : ''}`);
  }

  public studentStatus(student: IBasePerson, termCode?: string): Observable<IStudentStatus> {
    return this.http.get<IStudentStatus>(`${this.repoUrl}/${student.pidm}/student${termCode ? '?term=' + termCode : ''}`);
  }

  public getComments(pidm: number): Observable<Array<IComment>> {
    return this.http.get<Array<IComment>>(`${this.repoUrl}/${pidm}/comments`);
  }
  
  public postComment(pidm: number, comment: IComment): Observable<IId> {
    return this.http.post<IId>(`${this.repoUrl}/${pidm}/comments`, comment);
  }
  
  public getFciStatus(student: IBasePerson, termCode: string): Observable<IDescription> {
    return this.http.get<IDescription>(`${this.repoUrl}/${student.pidm}/fci-status?term=${termCode}`);
  }

  public findAcademicStatus(student: IBasePerson, termCode: string): Observable<IAcademicStatus> {
    return this.http.get<IAcademicStatus>(`${this.repoUrl}/${student.pidm}/academic-status?term=${termCode}`);
  }

  public getProgramInformation(student: IBasePerson, termCode: string): Observable<IStudentProgramList> {
    return this.http.get<IStudentProgramList>(`${this.repoUrl}/${student.pidm}/programs?termCode=${termCode}`);
  }

  public getCourseHistory(pidm: number, levelCode: string, termCode: string): Observable<Array<IAuditCourseHistory>> {
    return this.http.get<Array<IAuditCourseHistory>>(`${this.repoUrl}/${pidm}/course-history`, withParams({levelCode: levelCode, termCode: termCode}));
  }

  public getTestScores(pidm: number): Observable<Array<ITest>> {
    return this.http.get<Array<ITest>>(`${this.repoUrl}/${pidm}/test-scores`);
  }

  public findSanctions(pidm: number, termCode: string, officeTypeId: number): Observable<ISanctionSummary> {
    return this.http.get<ISanctionSummary>(`${this.repoUrl}/${pidm}/sanctions`, withParams({ term: termCode, officeTypeId }));
  }

  public findPriorColleges(pidm: number): Observable<IPriorColleges> {
    return this.http.get<IPriorColleges>(`${this.repoUrl}/${pidm}/prior-colleges`);
  }

  public findHighSchoolTranscripts(pidm: number): Observable<Array<IHighSchoolTranscript>> {
    return this.http.get<Array<IHighSchoolTranscript>>(`${this.repoUrl}/${pidm}/high-school-transcripts`);
  }

  public findDismissedFromPreviousSchool(pidm: number, term: string, appNum: string): Observable<IAdmissionsQuestion> {
    return this.http.get<IAdmissionsQuestion>(`${this.repoUrl}/${pidm}/dismissed`, withParams({term, appNum}));
  }

  public findAllStudentInfo(pidm: number): Observable<Array<LibertyStudentSummary>> {
    return this.http.get<Array<LibertyStudentSummary>>(`${this.repoUrl}/${pidm}/all-student`);
  }

  public getAdmissionsTestScores(pidm: number): Observable<Array<ITestScore>> {
    return this.http.get<Array<ITestScore>>(`${this.repoUrl}/${pidm}/admissions-test-scores`);
  }

  public getAdmissionsDeposit(pidm: number, termCode: string): Observable<IAdmissionsDeposit> {
    return this.http.get<IAdmissionsDeposit>(`${this.repoUrl}/${pidm}/admissions-deposit?termCode=${termCode}`);
  }

  public getEfc(pidm: number, termCode: string): Observable<IDescription> {
    return this.http.get<IDescription>(`${this.repoUrl}/${pidm}/efc?termCode=${termCode}`);
  }
  
  public getAdvisors(pidm: number, termCode: string): Observable<Array<IAdvisor>> {
    return this.http.get<Array<IAdvisor>>(`${this.repoUrl}/${pidm}/advisors`, withParams({termCode: termCode}));
  }

  public hasProgramDismissal(pidm: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.repoUrl}/${pidm}/has-program-dismissal`);
  }

  public isLuoaStudent(pidm: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.repoUrl}/${pidm}/is-luoa-student`);
  }
}
